// ============================================================================
// COMPONENT - monacoCodeEditor.jsx
// ============================================================================
// client/src/components/monacoCodeEditor.jsx

import React, { useRef, useState } from 'react';
import Editor from '@monaco-editor/react';

import './monaCodeditor.css';

const MonacoCodeEditor = ({
    code,
    onChange,
    onSave,
    onDownload,
    onReload,
    onSerialInput,
    readOnly = false,
    disabled = false,
    fileName = 'sketch'
    }) => {
    const editorRef = useRef(null);
    const [lineCount, setLineCount] = useState(0);
    const [charCount, setCharCount] = useState(0);

    const [serialInput, setSerialInput] = useState('');

    // Handle editor mount
    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        
        // Set initial stats
        updateStats(code);

        // Add keyboard shortcuts
        editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
        if (onSave && !disabled) {
            onSave();
        }
        });

        // Register Arduino/C++ language configuration
        monaco.languages.register({ id: 'arduino' });
        monaco.languages.setMonarchTokensProvider('arduino', {
        keywords: [
            'void', 'int', 'float', 'char', 'boolean', 'String', 'byte',
            'if', 'else', 'for', 'while', 'do', 'switch', 'case', 'break',
            'return', 'continue', 'setup', 'loop', 'pinMode', 'digitalWrite',
            'digitalRead', 'analogRead', 'analogWrite', 'delay', 'millis',
            'Serial', 'begin', 'print', 'println', 'available', 'read',
            'HIGH', 'LOW', 'INPUT', 'OUTPUT', 'INPUT_PULLUP', 'true', 'false'
        ],
        typeKeywords: [
            'void', 'int', 'float', 'char', 'boolean', 'String', 'byte', 
            'unsigned', 'long', 'short', 'double'
        ],
        operators: [
            '=', '>', '<', '!', '~', '?', ':', '==', '<=', '>=', '!=',
            '&&', '||', '++', '--', '+', '-', '*', '/', '&', '|', '^', '%',
            '<<', '>>', '>>>', '+=', '-=', '*=', '/=', '&=', '|=', '^=',
            '%=', '<<=', '>>=', '>>>='
        ],
        symbols: /[=><!~?:&|+\-*\/\^%]+/,
        escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,
        tokenizer: {
            root: [
            [/[a-z_$][\w$]*/, {
                cases: {
                '@typeKeywords': 'keyword',
                '@keywords': 'keyword',
                '@default': 'identifier'
                }
            }],
            [/[A-Z][\w\$]*/, 'type.identifier'],
            { include: '@whitespace' },
            [/[{}()\[\]]/, '@brackets'],
            [/[<>](?!@symbols)/, '@brackets'],
            [/@symbols/, {
                cases: {
                '@operators': 'operator',
                '@default': ''
                }
            }],
            [/\d*\.\d+([eE][\-+]?\d+)?/, 'number.float'],
            [/0[xX][0-9a-fA-F]+/, 'number.hex'],
            [/\d+/, 'number'],
            [/[;,.]/, 'delimiter'],
            [/"([^"\\]|\\.)*$/, 'string.invalid'],
            [/"/, { token: 'string.quote', bracket: '@open', next: '@string' }],
            [/'[^\\']'/, 'string'],
            [/'/, 'string.invalid']
            ],
            string: [
            [/[^\\"]+/, 'string'],
            [/@escapes/, 'string.escape'],
            [/\\./, 'string.escape.invalid'],
            [/"/, { token: 'string.quote', bracket: '@close', next: '@pop' }]
            ],
            whitespace: [
            [/[ \t\r\n]+/, 'white'],
            [/\/\*/, 'comment', '@comment'],
            [/\/\/.*$/, 'comment']
            ],
            comment: [
            [/[^\/*]+/, 'comment'],
            [/\/\*/, 'comment', '@push'],
            ["\\*/", 'comment', '@pop'],
            [/[\/*]/, 'comment']
            ]
        }
        });

        // ✅ ADD THIS: Register document formatting provider for C++
        monaco.languages.registerDocumentFormattingEditProvider('cpp', {
            provideDocumentFormattingEdits(model) {
                const text = model.getValue();
                const formatted = formatArduinoCode(text);
                
                return [
                    {
                        range: model.getFullModelRange(),
                        text: formatted
                    }
                ];
            }
        });
    };

    // ✅ IMPROVED: Format function for Arduino/C++ code
    const formatArduinoCode = (code) => {
        const lines = code.split('\n');
        const formattedLines = [];
        let indentLevel = 0;

        lines.forEach((line) => {
            const trimmed = line.trim();
            
            // Skip empty lines
            if (!trimmed) {
                formattedLines.push('');
                return;
            }

            // Count opening and closing braces
            const openBraces = (trimmed.match(/{/g) || []).length;
            const closeBraces = (trimmed.match(/}/g) || []).length;

            // Decrease indent BEFORE the line if it starts with }
            if (trimmed.startsWith('}')) {
                indentLevel = Math.max(0, indentLevel - closeBraces);
            }

            // Add indentation
            const indent = '  '.repeat(indentLevel);
            formattedLines.push(indent + trimmed);

            // Adjust indent for next line
            if (trimmed.startsWith('}')) {
                // Already decreased above, now add any opening braces on same line
                indentLevel += openBraces;
            } else {
                // Increase for opening braces, decrease for closing braces
                indentLevel += openBraces - closeBraces;
                indentLevel = Math.max(0, indentLevel);
            }
        });

        return formattedLines.join('\n');
    };

    // Handle editor change
    const handleEditorChange = (value) => {
        if (onChange) {
        onChange(value || '');
        }
        updateStats(value || '');
    };

    // Update line and character count
    const updateStats = (value) => {
        if (value) {
        setLineCount(value.split('\n').length);
        setCharCount(value.length);
        } else {
        setLineCount(0);
        setCharCount(0);
        }
    };

    // Format code
    const handleFormatCode = () => {
        if (editorRef.current) {
        editorRef.current.getAction('editor.action.formatDocument').run();
        }
    };

    // Find/Replace
    const handleFindReplace = () => {
        if (editorRef.current) {
        editorRef.current.getAction('actions.find').run();
        }
    };

    // ✅ Handle serial input submission
    const handleSerialSubmit = (e) => {
        e.preventDefault();
        if (onSerialInput && serialInput.trim()) {
            onSerialInput(serialInput);
            setSerialInput(''); // Clear after sending
        }
    };

    return (
        <div className="monaco-code-editor">
        <div className="editor-header">
            <div className="editor-title">
            <h3>💻 Arduino Code Editor</h3>
            <span className="file-name">{fileName}.ino</span>
            </div>
            
            <div className="editor-controls">
            <button
                className="editor-action-btn"
                onClick={handleFormatCode}
                disabled={disabled}
                title="Format Code (Shift+Alt+F)"
            >
                🎨 Format
            </button>
            
            <button
                className="editor-action-btn"
                onClick={handleFindReplace}
                disabled={disabled}
                title="Find/Replace (Ctrl+F)"
            >
                🔍 Find
            </button>

            {onSave && (
                <button
                    className="editor-action-btn"
                    onClick={onSave}
                    disabled={disabled || !code?.trim()}
                    title="Find/Replace (Ctrl+F)"
                >
                    💾 Save Code
                </button>
            )}

            {onDownload && (
                <button
                    className="editor-action-btn"
                    onClick={onDownload}
                    disabled={!code?.trim()}
                    title="Find/Replace (Ctrl+F)"
                >
                    📥 Download
                </button>
            )}

            {onReload && (
                <button
                    className="editor-action-btn"
                    onClick={onReload}
                    disabled={disabled}
                    title="Reload from templates"
                >
                    🔄 Regenerate
                </button>
            )}
            </div>
        </div>

        <div className="monaco-editor-wrapper">
            <Editor
                height="100%"
                defaultLanguage="cpp"
                language="cpp"
                theme="vs-dark"
                value={code}
                onChange={handleEditorChange}
                onMount={handleEditorDidMount}
                options={{
                    minimap: { 
                    enabled: true,
                    scale: 1,
                    showSlider: 'mouseover'
                    },
                    fontSize: 14,
                    fontFamily: "'Cascadia Code', 'Fira Code', 'Consolas', 'Courier New', monospace",
                    fontLigatures: true,
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    tabSize: 2,
                    insertSpaces: true,
                    wordWrap: 'on',
                    formatOnPaste: true,
                    formatOnType: true,
                    suggestOnTriggerCharacters: true,
                    quickSuggestions: {
                    other: true,
                    comments: false,
                    strings: false
                    },
                    acceptSuggestionOnEnter: 'on',
                    readOnly: readOnly,
                    renderWhitespace: 'selection',
                    bracketPairColorization: {
                    enabled: true
                    },
                    guides: {
                    bracketPairs: true,
                    indentation: true
                    },
                    smoothScrolling: true,
                    cursorBlinking: 'smooth',
                    cursorSmoothCaretAnimation: true,
                    scrollbar: {
                    vertical: 'visible',           // ✅ Changed
                    horizontal: 'visible',         // ✅ Changed
                    useShadows: false,             // ✅ Changed
                    verticalScrollbarSize: 12,     // ✅ Changed
                    horizontalScrollbarSize: 12,   // ✅ Changed
                    alwaysConsumeMouseWheel: false // ✅ Added
                    },
                    mouseWheelZoom: true,
                    contextmenu: true,
                    copyWithSyntaxHighlighting: true
                }}
                loading={
                    <div className="monaco-loading">
                    <div className="loader" />
                    <p>Loading Monaco Editor...</p>
                    </div>
                }
            />
        </div>

        {/* SERIAL INPUT SECTION */}
        {onSerialInput && (
            <div className="serial-input-section">
                <form onSubmit={handleSerialSubmit}>
                    <input
                        type="text"
                        value={serialInput}
                        onChange={(e) => setSerialInput(e.target.value)}
                        placeholder="Type serial input and press Enter..."
                        disabled={disabled}
                        className="serial-input-field"
                    />
                    <button
                        type="submit"
                        disabled={disabled || !serialInput.trim()}
                        className="serial-send-btn"
                    >
                        📤 Send
                    </button>
                </form>
            </div>
        )}

        <div className="editor-footer">
            <div className="editor-stats">
            <span>📏 Lines: {lineCount}</span>
            <span>📝 Characters: {charCount}</span>
            <span>💾 Language: Arduino (C++)</span>
            </div>
            
            <div className="editor-hints">
            <span>💡 Ctrl+S to save</span>
            <span>💡 Ctrl+F to find</span>
            <span>💡 Shift+Alt+F to format</span>
            </div>
        </div>
        </div>
    );
};

export default MonacoCodeEditor;